import React from 'react';
import { Text, TextStyle, TextProps } from 'react-native';
import { useAccessibility } from '../../hooks/useAccessibility';

interface AccessibleTextProps extends TextProps {
  style?: TextStyle | TextStyle[];
  children: React.ReactNode;
  variant?: 'h1' | 'h2' | 'h3' | 'body' | 'caption' | 'button';
}

const AccessibleText: React.FC<AccessibleTextProps> = ({
  style,
  children,
  variant = 'body',
  ...props
}) => {
  const { settings } = useAccessibility();

  // Base font sizes
  const baseFontSizes = {
    h1: 16, // Headers
    h2: 16, // Headers
    h3: 16, // Headers
    body: 12, // Normal text
    caption: 14, // Sub-headers
    button: 14, // Sub-headers
  };

  // Increase font size for large text setting
  const fontSizes = settings.largeText
    ? {
        h1: baseFontSizes.h1 * 1.25,
        h2: baseFontSizes.h2 * 1.25,
        h3: baseFontSizes.h3 * 1.25,
        body: baseFontSizes.body * 1.25,
        caption: baseFontSizes.caption * 1.25,
        button: baseFontSizes.button * 1.25,
      }
    : baseFontSizes;

  // Font weights
  const fontWeights: Record<string, TextStyle['fontWeight']> = {
    h1: 'bold',
    h2: 'bold',
    h3: '600',
    body: 'normal',
    caption: 'normal',
    button: '600',
  };

  // Base styles
  const baseStyle: TextStyle = {
    fontSize: fontSizes[variant],
    fontWeight: fontWeights[variant],
    color: settings.highContrast ? '#000000' : '#1F2937',
  };

  // Apply high contrast if needed
  if (settings.highContrast) {
    baseStyle.color = '#000000';
  }

  return (
    <Text
      style={[baseStyle, style]}
      {...props}
      accessibilityRole={variant.startsWith('h') ? 'header' : undefined}
    >
      {children}
    </Text>
  );
};

export default AccessibleText;
